/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  output,
  viewChild,
  contentChildren,
  input,
  linkedSignal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import type { AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@angular/forms';

import { VitamUIFieldErrorComponent } from '../vitamui-field-error/vitamui-field-error.component';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'editable-field-component',
  host: {
    '[class.readonly]': 'disabled()',
  },
})
export class EditableFieldComponent implements OnChanges, AfterContentInit, ControlValueAccessor {
  protected elementRef: ElementRef;
  readonly #changeDetectorRef = inject(ChangeDetectorRef, { optional: true });

  constructor(elementRef: ElementRef = inject(ElementRef)) {
    this.elementRef = elementRef;
  }

  readonly label = input<string>(undefined);

  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly disabledInput = input(false, { alias: 'disabled', transform: coerceBooleanProperty });
  readonly disabled = linkedSignal(() => this.disabledInput());

  readonly validator = input<ValidatorFn>(undefined);

  readonly asyncValidator = input<AsyncValidatorFn>(undefined);

  readonly editOpen = output<void>();
  readonly editClose = output<boolean>();

  readonly cdkConnectedOverlay = viewChild(CdkConnectedOverlay);
  readonly errors = contentChildren(VitamUIFieldErrorComponent);

  control = new FormControl();
  originValue: any;
  editMode = false;
  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  get showSpinner(): boolean {
    return this.control.pending && this.control.dirty;
  }
  get canConfirm() {
    return this.editMode && !this.control.pending && this.control.valid && this.control.dirty;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['validator']) {
      this.control.setValidators(this.validator());
      this.control.setErrors(null);
    }
    if (changes['asyncValidator']) {
      this.control.setAsyncValidators(this.asyncValidator());
      this.control.setErrors(null);
    }
  }

  ngAfterContentInit() {
    this.control.statusChanges.subscribe(() => {
      this.errors().forEach((error: VitamUIFieldErrorComponent) => {
        error.show = this.control.errors ? !!this.control.errors[error.errorKey()] : false;
      });
      // Status may change asynchronously (async validators): refresh spinner/confirm state (zoneless)
      this.#changeDetectorRef?.markForCheck();
    });
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: any) {
    this.control.reset(value, { emitEvent: false });
    this.originValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled.set(disabled);
  }

  enterEditMode() {
    this.editMode = true;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.cancel();
  }

  @HostListener('keydown.enter', ['$event'])
  onEnter(event: Event) {
    (event as KeyboardEvent).preventDefault();
    this.confirm();
  }

  @HostListener('document:click', ['$event.target'])
  onClick(target: EventTarget) {
    const htmlTarget = target as HTMLElement;
    if (!this.editMode) {
      return;
    }
    const overlayRef = this.cdkConnectedOverlay().overlayRef;
    if (this.isInside(htmlTarget, this.elementRef.nativeElement) || this.isInside(htmlTarget, overlayRef.hostElement)) {
      return;
    }
    this.cancel();
  }

  confirm() {
    if (!this.canConfirm) {
      return;
    }
    this.editMode = false;
    this.onChange(this.control.value);
    this.originValue = this.control.value;
    this.control.reset(this.originValue);
  }

  cancel() {
    if (!this.editMode) {
      return;
    }
    this.editMode = false;
    this.control.reset(this.originValue);
  }

  protected isInside(target: HTMLElement, element: HTMLElement): boolean {
    return element && (target === element || element.contains(target));
  }
}
